import * as React from 'react';

import {Stack, Typography} from '@mui/material';
import {AggregationSurveyResponsesSeries} from 'crqlar-react-common/dist/models/aggregation/surveyAggregation.model';
import {useTranslation} from 'react-i18next';

import {CardHeadline} from '../../../insights/kit/Headline';
import {CHART_HEIGHT} from "../../../insights/kit/insightsChart";
import {SeriesCard} from '../../../insights/kit/SeriesCard';
import {calendarDayToTimestamp, formatFullDate, useInsightsFormatters, Verdict, verdictColor} from "../../../insights/kit/insightsFormat";
import {InsightsLoading} from '../../../insights/kit/insightsLoading';
import {InsightsSeriesChart, InsightsSeriesLegend, SeriesColors, SeriesSpec} from '../charts/InsightsSeriesChart';
import {InsightsColumn, InsightsTable} from '../../../insights/kit/InsightsTable';
import {TooltipRow} from '../../../insights/kit/InsightsTooltip';

interface ResponseDayRow {
    index: number;
    timestamp: number | null;
    compareTimestamp: number | null;
    responses: number | null;
    invited: number | null;
    rate: number | null;
    compareResponses: number | null;
    verdict: Verdict;
}

interface ResponsesPerDayCardProps {
    series?: AggregationSurveyResponsesSeries;
    loading?: InsightsLoading;
    height?: number;
}

function toRows(series: AggregationSurveyResponsesSeries | undefined, target: number | undefined): ResponseDayRow[] {
    if (series == null) return [];

    const current = series.current.points;
    const compare = series.compare?.points ?? [];
    const length = Math.max(current.length, compare.length);

    return Array.from({length}, (_, index) => {
        const point = current[index];
        const comparePoint = compare[index];

        return {
            index,
            timestamp: point == null ? null : calendarDayToTimestamp(point.day),
            compareTimestamp: comparePoint == null ? null : calendarDayToTimestamp(comparePoint.day),
            responses: point?.responses ?? null,
            invited: point?.invitations ?? null,
            rate: point == null || point.invitations === 0 ? null : (point.responses / point.invitations) * 100,
            compareResponses: comparePoint?.responses ?? null,
            verdict:
                point == null || target == null ? 'neutral' : point.responses >= target ? 'positive' : 'negative',
        };
    });
}

interface DayTableLabels {
    date: string;
    compareDate: string;
    responses: string;
    compare: string;
    invited: string;
    rate: string;
}

interface DayTableProps {
    rows: ReadonlyArray<ResponseDayRow>;
    labels: DayTableLabels;
    hasCompare: boolean;
    formatCount: (value: number) => string;
    formatPercent: (value: number) => string;
}

function DayTable({rows, labels, hasCompare, formatCount, formatPercent}: DayTableProps) {
    const ordered = React.useMemo(() => [...rows].reverse(), [rows]);

    const columns: Array<InsightsColumn<ResponseDayRow> | false> = [
        {key: 'date', kind: 'label', label: labels.date, cell: row => (row.timestamp == null ? '—' : formatFullDate(row.timestamp))},
        {
            key: 'responses',
            kind: 'verdict',
            label: labels.responses,
            cell: row => row.responses,
            format: formatCount,
            color: row => verdictColor(row.verdict),
        },
        hasCompare && {
            key: 'compareDate',
            kind: 'text',
            label: labels.compareDate,
            cell: row => (row.compareTimestamp == null ? '—' : formatFullDate(row.compareTimestamp)),
            comparison: true,
        },
        hasCompare && {key: 'compare', kind: 'compare', label: labels.compare, cell: row => row.compareResponses, format: formatCount},
        {key: 'invited', kind: 'detail', label: labels.invited, cell: row => row.invited, format: formatCount},
        {key: 'rate', kind: 'detail', label: labels.rate, cell: row => row.rate, format: formatPercent},
    ];

    return <InsightsTable label={labels.responses} columns={columns} rows={ordered} rowKey={row => row.index}/>;
}

export function ResponsesPerDayCard({series, loading, height = CHART_HEIGHT}: ResponsesPerDayCardProps) {
    const {t} = useTranslation();

    const target = series?.summary.target ?? undefined;
    const rows = React.useMemo(() => toRows(series, target), [series, target]);

    const hasCompare = series?.compare != null;

    /*
     * Totals only compare between ranges of the same length; otherwise the change
     * is measured per day of each range, and the hover says so.
     */
    const sameLength = series?.compare != null && series.compare.points.length === series.current.points.length;
    const {
        count: formatCount,
        percent: formatPercent,
        decimal: formatDecimal,
        oneDecimal: formatPerDay,
    } = useInsightsFormatters();

    const chartLabels = React.useMemo(
        () => ({
            responses: t('Responses'),
            invited: t('Invited'),
            rate: t('Response rate'),
            compare: t('Compare period'),
            target: t('Target'),
            aboveTarget: t('At or above target'),
            belowTarget: t('Below target'),
        }),
        [t],
    );

    const tableLabels = React.useMemo<DayTableLabels>(
        () => ({
            date: t('Date'),
            compareDate: t('Compare date'),
            responses: chartLabels.responses,
            compare: chartLabels.compare,
            invited: chartLabels.invited,
            rate: chartLabels.rate,
        }),
        [chartLabels, t],
    );

    const yMax = React.useMemo(() => {
        const peak = rows.reduce(
            (max, row) => Math.max(max, row.responses ?? 0, row.compareResponses ?? 0),
            target ?? 0,
        );

        return Math.max(1, Math.ceil(peak * 1.15));
    }, [rows, target]);

    const summary = series?.summary;

    const change = summary == null || summary.previousResponses == null || summary.previousPerDay == null
        ? null
        : sameLength
            ? summary.responses - summary.previousResponses
            : summary.perDay - summary.previousPerDay;

    const formatChange = sameLength ? formatCount : formatDecimal;

    const headline = (
        // The card is called "Responses per day", so the figure under it is the per-day one.
        <CardHeadline
            value={summary == null ? '—' : formatPerDay(summary.perDay)}
            suffix={summary == null ? undefined : t('{{total}} in period', {total: formatCount(summary.responses)})}
            change={summary == null || summary.previousResponses == null || summary.previousPerDay == null ? null : {
                title: chartLabels.responses,
                // The headline is a per-day figure, so the hover has to say which figure it compares.
                subtitle: sameLength ? t('Total in period') : t('Per day, as the two ranges differ in length'),
                current: sameLength ? formatCount(summary.responses) : formatDecimal(summary.perDay),
                previous: sameLength ? formatCount(summary.previousResponses) : formatDecimal(summary.previousPerDay),
                delta: change,
                formatDelta: formatChange,
                compareLabel: chartLabels.compare,
            }}
        />
    );

    const tooltipRows = (row: ResponseDayRow, colors: SeriesColors) => (
        <React.Fragment>
            {row.responses != null && (
                <TooltipRow swatch={colors.series} label={chartLabels.responses} value={formatCount(row.responses)}/>
            )}

            {row.compareResponses != null && (
                <TooltipRow
                    swatch={colors.compare}
                    label={
                        row.compareTimestamp == null
                            ? chartLabels.compare
                            : `${chartLabels.compare} · ${formatFullDate(row.compareTimestamp)}`
                    }
                    value={formatCount(row.compareResponses)}
                />
            )}

            {row.invited != null && <TooltipRow label={chartLabels.invited} value={formatCount(row.invited)}/>}
            {row.rate != null && <TooltipRow label={chartLabels.rate} value={formatPercent(row.rate)}/>}

            {target != null && row.responses != null && (
                <TooltipRow swatch={colors.target} label={chartLabels.target} value={`≥ ${formatCount(target)}`}/>
            )}
        </React.Fragment>
    );

    const spec: SeriesSpec<ResponseDayRow> = {
        rows,
        valueKey: 'responses',
        compareKey: hasCompare ? 'compareResponses' : undefined,
        label: chartLabels.responses,
        compareLabel: chartLabels.compare,
        target: target == null ? undefined : {
            value: target,
            verdict: 'split',
            label: `${chartLabels.target} ${formatCount(target)}`,
            aboveLabel: chartLabels.aboveTarget,
            belowLabel: chartLabels.belowTarget,
        },
    };

    return (
        <SeriesCard
            title={t('Responses per day')}
            hint={t('Replies arriving each day, beside the invitations that went out. The line runs green above the daily target and red below it, so the stretches that kept up are the green ones. The dashed line is the compare period, laid alongside day by day.')}
            headline={headline}
            loading={loading}
            legend={<InsightsSeriesLegend {...spec}/>}
            empty={rows.length === 0 ? (
                <Typography variant={'body2'} color={'text.secondary'}>
                    {t('No responses in this period.')}
                </Typography>
            ) : undefined}
            chart={(
                <InsightsSeriesChart
                    {...spec}
                    height={height}
                    domain={[0, yMax]}
                    formatTick={formatCount}
                    tooltip={tooltipRows}
                />
            )}
            table={(
                <DayTable
                    rows={rows}
                    labels={tableLabels}
                    hasCompare={hasCompare}
                    formatCount={formatCount}
                    formatPercent={formatPercent}
                />
            )}
        />
    );
}
